import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pluginApi, serverApi } from '@/lib/api';
import { Shield, Trash2, Plus, AlertCircle, CheckCircle2, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface BlacklistedServer {
  id: string;
  discordServerId: string;
  name?: string;
  reason: string;
  active: boolean;
  createdAt: string;
}

export function ServerBlacklistPlugin({ serverId }: { serverId: string }) {
  const queryClient = useQueryClient();

  const { data: plugins = [] } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const { data: plugin } = useQuery({
    queryKey: ['servers', serverId, 'plugins', 'server-blacklist'],
    queryFn: () => serverApi.getPluginConfig(serverId, 'server-blacklist'),
  });

  const serverBlacklistPlugin = (plugins as any[]).find((item) => item.pluginId === 'server-blacklist');
  const isEnabled = serverBlacklistPlugin?.enabled ?? false;

  const togglePluginMutation = useMutation({
    mutationFn: async (enabled: boolean) =>
      enabled ? pluginApi.enablePlugin(serverId, 'server-blacklist') : pluginApi.disablePlugin(serverId, 'server-blacklist'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'plugins'] });
      toast.success('Server Blacklist plugin aggiornato');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Errore aggiornamento plugin Server Blacklist');
    },
  });

  const [newServerId, setNewServerId] = useState('');
  const [newReason, setNewReason] = useState('');
  const [loading, setLoading] = useState(false);

  const blacklistedServers: BlacklistedServer[] = [
    {
      id: '1',
      discordServerId: '123456789012345678',
      name: 'Spam Server',
      reason: 'Spamming and harassment',
      active: true,
      createdAt: '2024-02-01T10:00:00Z',
    },
    {
      id: '2',
      discordServerId: '987654321098765432',
      name: 'Raid Community',
      reason: 'Organized raid attempts',
      active: true,
      createdAt: '2024-02-02T15:30:00Z',
    },
  ];

  const handleAddServer = async () => {
    if (!newServerId.trim() || !newReason.trim()) {
      toast.error('Please enter both Server ID and Reason');
      return;
    }

    if (!/^\d{18}$/.test(newServerId)) {
      toast.error('Server ID must be 18 digits');
      return;
    }

    setLoading(true);
    try {
      toast.info('Use Discord command: /blacklist-add [id] [reason]');
      setNewServerId('');
      setNewReason('');
    } catch (error) {
      toast.error('Error adding server to blacklist');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveServer = (id: string) => {
    toast.info('Use Discord command: /blacklist-remove [id]');
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      {/* Header */}
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-red-500/20 rounded-xl">
            <Shield className="w-6 h-6 text-red-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Server Blacklist</h3>
            <p className="text-sm text-muted-foreground">
              Manage blacklist of problematic servers and restrict access
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          disabled={togglePluginMutation.isPending}
          onCheckedChange={(enabled) => togglePluginMutation.mutate(enabled)}
        />
      </div>

      {/* Quick Info */}
      <Card className="bg-red-500/10 border-red-500/30">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-400" />
            How Blacklist Works
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div>
              <p className="font-semibold text-sm mb-1">🚫 Level 1: Immediate Warning</p>
              <p className="text-sm text-muted-foreground">
                Users who join your server and are in a blacklisted server receive a DM warning
              </p>
            </div>

            <div>
              <p className="font-semibold text-sm mb-1">🔒 Level 2: Dashboard Block</p>
              <p className="text-sm text-muted-foreground">
                Users in blacklisted servers cannot access the dashboard (403 Forbidden)
              </p>
            </div>

            <div>
              <p className="font-semibold text-sm mb-1">✅ Recovery</p>
              <p className="text-sm text-muted-foreground">
                Users regain access automatically after leaving all blacklisted servers
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Server Form */}
      <Card>
        <CardHeader>
          <CardTitle>Add Server to Blacklist</CardTitle>
          <CardDescription>Only accessible to server administrators</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="server-id">Discord Server ID</Label>
            <Input
              id="server-id"
              placeholder="123456789012345678"
              value={newServerId}
              onChange={(e) => setNewServerId(e.target.value)}
              maxLength={18}
            />
            <p className="text-xs text-muted-foreground">
              Server ID must be 18 digits. Right-click server → Copy Server ID
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Blacklist</Label>
            <Textarea
              id="reason"
              placeholder="Spamming and harassment, Raid attempts, Hate speech, etc."
              value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
              className="min-h-24"
            />
            <p className="text-xs text-muted-foreground">
              Be specific - this is shown to affected users
            </p>
          </div>

          <div className="pt-2">
            <p className="text-sm font-semibold mb-3 text-muted-foreground">
              Use Discord command instead:
            </p>
            <div className="bg-black/20 p-3 rounded-lg flex items-center justify-between">
              <code className="text-sm text-muted-foreground">
                /blacklist-add [server_id] [reason]
              </code>
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  copyToClipboard('/blacklist-add [server_id] [reason]')
                }
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Blacklist */}
      <Card>
        <CardHeader>
          <CardTitle>Current Blacklist ({blacklistedServers.length})</CardTitle>
          <CardDescription>Active blacklisted servers in this server</CardDescription>
        </CardHeader>
        <CardContent>
          {blacklistedServers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Shield className="w-12 h-12 text-muted-foreground/30 mx-auto mb-2" />
              <p>No servers blacklisted yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {blacklistedServers.map((server) => (
                <div
                  key={server.id}
                  className="flex items-start justify-between p-4 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold">{server.name || 'Unknown Server'}</p>
                      {server.active && (
                        <span className="text-xs bg-red-500/30 text-red-300 px-2 py-0.5 rounded">
                          Active
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      ID: {server.discordServerId}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Reason: {server.reason}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Added: {new Date(server.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-400 hover:text-red-300"
                    onClick={() => handleRemoveServer(server.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Command Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Command Reference</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add Command */}
          <div className="border-l-4 border-red-500 pl-4 py-2">
            <p className="font-semibold text-sm mb-1">/blacklist-add [id] [reason]</p>
            <p className="text-sm text-muted-foreground mb-2">
              Add a server to the blacklist (Admin only)
            </p>
            <div className="bg-black/20 p-3 rounded text-xs text-muted-foreground font-mono">
              /blacklist-add 123456789012345678 Spamming and harassment
            </div>
          </div>

          {/* Remove Command */}
          <div className="border-l-4 border-yellow-500 pl-4 py-2">
            <p className="font-semibold text-sm mb-1">/blacklist-remove [id]</p>
            <p className="text-sm text-muted-foreground mb-2">
              Remove a server from blacklist (Admin only)
            </p>
            <div className="bg-black/20 p-3 rounded text-xs text-muted-foreground font-mono">
              /blacklist-remove 123456789012345678
            </div>
          </div>

          {/* List Command */}
          <div className="border-l-4 border-green-500 pl-4 py-2">
            <p className="font-semibold text-sm mb-1">/blacklist-list</p>
            <p className="text-sm text-muted-foreground mb-2">
              View all blacklisted servers (Admin only)
            </p>
            <div className="bg-black/20 p-3 rounded text-xs text-muted-foreground font-mono">
              Shows ID, name, reason, and dates for all blacklisted servers
            </div>
          </div>

          {/* Check Command */}
          <div className="border-l-4 border-blue-500 pl-4 py-2">
            <p className="font-semibold text-sm mb-1">/blacklist-check @user</p>
            <p className="text-sm text-muted-foreground mb-2">
              Check if a user is in blacklisted servers
            </p>
            <div className="bg-black/20 p-3 rounded text-xs text-muted-foreground font-mono">
              Returns user's status and list of blacklisted servers if applicable
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Warning Examples */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">📋 Example Blacklist Reasons</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p className="text-muted-foreground">
            <strong>Harassment:</strong> "Spamming and harassment of members"
          </p>
          <p className="text-muted-foreground">
            <strong>Raiding:</strong> "Organized raid attempts on server"
          </p>
          <p className="text-muted-foreground">
            <strong>Illegal:</strong> "Distribution of illegal content"
          </p>
          <p className="text-muted-foreground">
            <strong>Scam:</strong> "Cryptocurrency scam and phishing operations"
          </p>
          <p className="text-muted-foreground">
            <strong>Hate:</strong> "Hate speech and discrimination"
          </p>
          <p className="text-muted-foreground">
            <strong>Spam:</strong> "Advertising spam and bot networks"
          </p>
        </CardContent>
      </Card>

      {/* Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            Plugin Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Server Blacklist is <span className="font-semibold text-green-400">enabled</span> and protecting your server.
            {blacklistedServers.length > 0 && (
              <span>
                {' '}Currently blocking <span className="font-semibold">{blacklistedServers.length}</span> server(s).
              </span>
            )}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
